import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Header from "./Header";
import OnboardingHeader from "./OnboardingHeader";
import Sidebar from "./Sidebar";
import LoginAppSelectionModal from "@/components/auth/LoginAppSelectionModal";
import TrialBanner from "@/components/auth/TrialBanner";
import ImageTrialBanner from "@/components/billing/ImageTrialBanner";
import AddRicherSignalsCard from "@/components/dashboard/AddRicherSignalsCard";
import ConnectStoreCard from "@/components/dashboard/ConnectStoreCard";
import { useApps, useSelectApp } from "@/hooks/useApps";
import { usePageTitleText } from "@/hooks/usePageTitle";
import { useSelectedAppContext } from "@/lib/useSelectedAppContext";

/**
 * App layout shell.
 *
 * Source: BRIEF_ia-redesign-frontend-implementation § 5.3 + ENG-AUTH-LOGIN-SIGNUP.
 *
 * - Renders TrialBanner (auth-derived), Header, Sidebar.
 * - Loads workspace apps on mount.
 * - Trigger logic:
 *     0 apps  → render Home with the "add your first app" empty state
 *               (no forced redirect — users can navigate freely and reach
 *               /onboarding via the CTA when they choose to).
 *     1 app   → auto-select; route to / (when not already there).
 *     2+ apps → renders LoginAppSelectionModal until selection completes.
 * - PO-9 post-modal landing: route to / after selection.
 *
 * Public auth routes (/login, /signup, /forgot-password, /reset-password,
 * /verify-email) are wired OUTSIDE this layout in App.tsx, so we no
 * longer carry the PUBLIC_ROUTES skip-gate that used to live here.
 *
 * The /onboarding route remains inside the authenticated shell — it's
 * for users who have signed up but haven't connected an app yet.
 *
 * **Upper-warning single mount (ENG-PO-DOCS-DIRECTIVES-0903 item A7,
 * experience-and-ui.md § 3a "Upper warnings — two surfaces, one look").**
 * `ConnectStoreCard` and `AddRicherSignalsCard` render ONCE here, above
 * every routed page's `<Outlet />`, instead of each page mounting its own
 * copy — this is the "shared page shell" the PO matrix's "EVERY page"
 * rule needs. `isHome` (the `/` Dashboard route) is resolved once from
 * `useLocation()` and passed down so both cards can honor the matrix's
 * Home-vs-other-pages split without each re-deriving the route.
 */

const ONBOARDING_ROUTES = ["/onboarding"];

function isOnboarding(pathname: string): boolean {
  return ONBOARDING_ROUTES.some(
    (route) => pathname === route || pathname.startsWith(`${route}/`),
  );
}

export default function Layout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  // Route-change focus + announcement (ENG-APP-UX-TUNEUP Track A, floor row
  // 2; experience-and-ui § Accessibility "Focus on navigation"). React Router
  // leaves focus on <body> after a client-side navigation, so a keyboard or
  // screen-reader user re-tabs the whole chrome on every page. The main
  // landmark is the focus target (tabindex -1: reachable programmatically and
  // from the skip link, never in the tab sequence) and a polite live region
  // names the page the user just reached.
  const mainRef = useRef<HTMLElement>(null);
  const pageTitle = usePageTitleText(location.pathname);
  const [announcement, setAnnouncement] = useState("");
  const announcedPathRef = useRef<string | null>(null);
  const {
    workspaceId,
    userId,
    userDisplayName,
    selectedAppId,
    setSelectedAppId,
  } = useSelectedAppContext();
  const { data: appsData, isLoading } = useApps(workspaceId, userId);
  const selectAppMutation = useSelectApp();
  const autoSelectedRef = useRef(false);

  const isOnOnboardingRoute = isOnboarding(location.pathname);
  const isHome = location.pathname === "/";
  // The Store connection page is the fix for the store-console notice, so
  // the notice never renders there (§ 12 § 3.1; the /settings alias
  // redirects to this path before the shell reads it).
  const isStoreConnectionRoute = location.pathname === "/app/store-connections";
  const apps = appsData?.apps ?? [];
  const appsLoaded = !isLoading && Boolean(appsData);

  // Derive modal state during render so we don't trigger a cascading
  // setState in the effect.
  const matchingApp = apps.find((a) => a.app_id === selectedAppId);
  const shouldShowModal =
    !isOnOnboardingRoute && appsLoaded && apps.length >= 2 && !matchingApp;
  // Upper-warning cards (A7) key off the selected app's platform — reuse
  // the same `matchingApp` lookup the modal-gate above already derives
  // rather than a second app-list scan.
  const upperWarningPlatform = matchingApp?.platform ?? null;

  // External-state effects: auto-select when exactly 1 app exists. The 0-app
  // case is NOT force-redirected to /onboarding — the user lands on Home and
  // sees an empty-state CTA pointing to /onboarding. The previous forced
  // redirect created a "loop-out trap" because /onboarding "Skip for now"
  // bounced users back here, which then re-redirected them to /onboarding.
  useEffect(() => {
    if (!appsLoaded) return;
    if (
      apps.length === 1 &&
      !autoSelectedRef.current &&
      apps[0].app_id !== selectedAppId &&
      userId !== null
    ) {
      autoSelectedRef.current = true;
      const onlyApp = apps[0];
      setSelectedAppId(onlyApp.app_id);
      selectAppMutation.mutate({ appId: onlyApp.app_id, userId });
    }
    // selectAppMutation excluded from deps intentionally; mutate is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appsLoaded, apps, navigate, selectedAppId, setSelectedAppId, userId]);

  useEffect(() => {
    if (announcedPathRef.current === location.pathname) return;
    announcedPathRef.current = location.pathname;
    mainRef.current?.focus();
    setAnnouncement(t("layout.routeAnnouncer.navigated", { title: pageTitle }));
  }, [location.pathname, pageTitle, t]);

  function handleSelectApp(appId: string) {
    if (userId === null) return;
    setSelectedAppId(appId);
    selectAppMutation.mutate({ appId, userId });
    navigate("/", { replace: true });
  }

  function handleAddNew() {
    navigate("/onboarding");
  }

  // Onboarding gets a minimal shell (slim header + no Sidebar / no app
  // gate). The slim header carries the brand mark + avatar dropdown so
  // freshly-signed-up users can still sign out from /onboarding.
  if (isOnOnboardingRoute) {
    return (
      <div className="min-h-screen bg-surface-alt">
        <SkipToContentLink label={t("layout.skipToContent")} />
        <OnboardingHeader />
        <TrialBanner />
        <ImageTrialBanner />
        <main
          id="main-content"
          ref={mainRef}
          tabIndex={-1}
          className="bg-surface focus-visible:outline-none"
        >
          <Outlet />
        </main>
        <RouteAnnouncer message={announcement} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-alt">
      <SkipToContentLink label={t("layout.skipToContent")} />
      <Header />
      <Sidebar />
      <main
        id="main-content"
        ref={mainRef}
        tabIndex={-1}
        className="ml-60 bg-surface pt-14 focus-visible:outline-none"
      >
        {/* Account banners (unverified email + its only "Resend email"
            button, trial countdown, trial ended, image trial) mount INSIDE
            main, under the fixed header's pt-14 offset. Rendered before
            <Header /> in normal flow they sat at y=0 beneath the fixed
            header, so a new user could neither see nor click them
            (new-user funnel proof 2026-10-17). The onboarding shell above
            keeps them after its in-flow header. */}
        <TrialBanner />
        <ImageTrialBanner />
        {/* Upper warnings (A7) — single shared mount, above every page's
            body. See the class doc comment above for the matrix + the
            single-mount rationale. */}
        <div className="space-y-4 px-6 pt-6">
          <ConnectStoreCard
            platform={upperWarningPlatform}
            isHome={isHome}
            onStoreConnectionPage={isStoreConnectionRoute}
          />
          <AddRicherSignalsCard
            platform={upperWarningPlatform}
            appId={selectedAppId}
            workspaceId={workspaceId}
            isHome={isHome}
          />
        </div>
        <Outlet />
      </main>
      <RouteAnnouncer message={announcement} />
      <LoginAppSelectionModal
        open={shouldShowModal}
        apps={apps}
        lastSelectedAppId={appsData?.last_selected_app_id ?? null}
        userDisplayName={userDisplayName}
        onSelect={handleSelectApp}
        onAddNew={handleAddNew}
      />
    </div>
  );
}

/**
 * The skip link (WCAG 2.4.1 Bypass Blocks): visually hidden until focused,
 * then pinned top-left; its target is the main landmark (`#main-content`).
 */
function SkipToContentLink({ label }: { label: string }) {
  return (
    <a
      href="#main-content"
      className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-surface focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-ink focus:ring-2 focus:ring-primary-500"
    >
      {label}
    </a>
  );
}

/** The polite live region that names the page after a route change. */
function RouteAnnouncer({ message }: { message: string }) {
  return (
    <div aria-live="polite" aria-atomic="true" className="sr-only">
      {message}
    </div>
  );
}
